import Picker, { type DatePickerProps } from "react-datepicker";
import type React from "react";
import { useId, type ReactNode } from "react";
import { css, cx } from "../../../../styled-system/css";
import type { SystemStyleObject } from "../../../../styled-system/types";
import { Label } from "react-aria-components";
import moment from "moment";
import { HStack } from "../../../../styled-system/jsx";
import { formField } from "../../../../styled-system/recipes";
import { Button } from "../../core/basic/Button";
import { add } from "date-fns";
import { useTranslation } from "react-i18next";
import { useDatepickerLocale } from "../../../i18n/useDatepickerLocale";

type Props = Omit<
  DatePickerProps,
  "selected" | "onChange" | "selectsRange" | "selectsMultiple" | "excludeScrollbar" | "onSelect" | "className"
> & {
  label?: ReactNode;
  labelTooltip?: string;
  value?: string | null;
  /**
   * @param date ISO 8601 date string
   */
  onChange?: (date: string | null) => void;
  css?: SystemStyleObject;
  inputClassName?: string;
  selectsMultiple?: never;
  selectsRange?: never;
  ignoreUtc?: boolean;
};

export function Datepicker({
  label,
  value,
  onChange,
  css: cssProp,
  inputClassName,
  ignoreUtc = false,
  locale,
  dateFormat,
  ...rest
}: Props) {
  const inputId = useId();
  const dpLocale = useDatepickerLocale();
  const effectiveLocale = locale ?? dpLocale;
  // Default to the locale's own short-date pattern; react-datepicker's default is US-ordered regardless of locale.
  const resolvedDateFormat =
    dateFormat ??
    (typeof effectiveLocale === "string" ? undefined : effectiveLocale.formatLong.date({ width: "short" }));
  const selected =
    value ?
      ignoreUtc ? moment(value, "YYYY-MM-DD").toDate()
      : new Date(Date.parse(value))
    : null;
  const classes = formField();

  return (
    <div className={cx(classes.fieldWrapper, css(cssProp))}>
      <Label htmlFor={inputId} className={classes.label}>
        {label}
      </Label>

      <Picker
        {...rest}
        showMonthYearDropdown={undefined} // This bypasses some weird type issue, see https://github.com/Hacker0x01/react-datepicker/issues/5198
        className={cx(
          css({
            _disabled: { cursor: "not-allowed", backgroundColor: "lightgrey" },
          }),
          inputClassName,
        )}
        locale={effectiveLocale}
        dateFormat={resolvedDateFormat}
        selected={selected}
        onChange={(date) =>
          onChange?.(
            date ?
              ignoreUtc ? moment(date).format("YYYY-MM-DD")
              : date.toISOString()
            : null,
          )
        }
      />
    </div>
  );
}

export function PeriodPicker({
  period,
  setPeriod,
  periodType,
}: {
  period: Date;
  setPeriod: React.Dispatch<React.SetStateAction<Date>>;
  periodType: "month" | "year";
}) {
  const { t } = useTranslation(["common"]);
  return (
    <HStack alignSelf={"center"}>
      <Button
        variant={"contentOnly"}
        aria-label={t(($) => $.common.actions["Previous"])}
        onPress={() => {
          setPeriod((prev) =>
            add(prev, {
              [`${periodType}s`]: -1,
            }),
          );
        }}
        className={css({
          alignSelf: "end",
          paddingY: "xsmall",
          color: "text.secondary",
          fontSize: "32px",
          fontWeight: "bold",
          lineHeight: "1",
        })}
      >
        {"<"}
      </Button>
      <Datepicker
        value={period.toISOString()}
        onChange={(val) => setPeriod(new Date(val!))}
        showMonthYearPicker={periodType === "month"}
        showYearPicker={periodType === "year"}
        dateFormat={periodType === "month" ? "yyyy, MMMM" : "yyyy"}
      />
      <Button
        variant={"contentOnly"}
        aria-label={t(($) => $.common.actions["Next"])}
        onPress={() => {
          setPeriod((prev) =>
            add(prev, {
              [`${periodType}s`]: 1,
            }),
          );
        }}
        className={css({
          alignSelf: "end",
          paddingY: "xsmall",
          color: "text.secondary",
          fontSize: "32px",
          fontWeight: "bold",
          lineHeight: "1",
        })}
      >
        {">"}
      </Button>
    </HStack>
  );
}
